import { createTheme } from "@mui/material/styles";

/**
 * 🎯 CENTRAL COLOR SYSTEM
 */
const COLORS = {
  brand: {
    primary: "#035c85",
    dark: "#024e5f",
    light: "#e6f4f7",
  },

  neutral: {
    50: "#f4f6f8",
    100: "#e9eef2",
    200: "#d9e2ec",
    300: "#cbd2d9",
    400: "#9aa5b1",
    500: "#7b8794",
    600: "#616e7c",
    700: "#52606d",
    800: "#3e4c59",
    900: "#1f2933",
  },

  status: {
    successBg: "#a1ffc3",
    success: "#035f40",
    warningBg: "#ffd488",
    warning: "#633706",
    errorBg: "#ff9c9c",
    error: "#940c0c",
    infoBg: "#a9d5f4",
    info: "#0a628e",
  },

  background: {
    default: "#f4f6f8",
    paper: "#ffffff",
  },

  text: {
    primary: "#1f2937",
    secondary: "#6b7280",
    tertiary: "#eeeeee",
    disabled: "#9aa5b1",
  },
};

/**
 * 🎨 THEME
 */
const theme = createTheme({
  palette: {
    mode: "light",

    primary: {
      main: COLORS.brand.primary,
      dark: COLORS.brand.dark,
      light: COLORS.brand.light,
      contrastText: "#ffffff",
    },

    secondary: {
      main: COLORS.neutral[600],
    },

    success: { main: COLORS.status.success },
    warning: { main: COLORS.status.warning },
    error: { main: COLORS.status.error },
    info: { main: COLORS.status.info },

    background: {
      default: COLORS.background.default,
      paper: COLORS.background.paper,
    },

    text: {
      primary: COLORS.text.primary,
      secondary: COLORS.text.secondary,
      disabled: COLORS.text.disabled,
    },
  },

  typography: {
    // fontFamily: "Inter, Roboto, sans-serif",
    fontFamily: "CG Times, serif",

    h1: {
      fontSize: "28px",
      fontWeight: 700,
      color: COLORS.neutral[900],
    },

    h2: {
      fontSize: "24px",
      fontWeight: 700,
      color: COLORS.neutral[900],
    },

    h3: {
      fontSize: "20px",
      fontWeight: 600,
      color: COLORS.neutral[800],
    },

    /**
     * 🏷️ PAGE TITLE SYSTEM (VERY IMPORTANT)
     */
    h4: {
      fontSize: "18px",
      fontWeight: 600,
      color: COLORS.neutral[800],
    },

    subtitle1: {
      fontSize: "16px",
      fontWeight: 500,
      color: COLORS.neutral[700],
    },

    body1: {
      fontSize: "14px",
      color: COLORS.text.primary,
    },

    body2: {
      fontSize: "13px",
      color: COLORS.text.secondary,
    },

    button: {
      textTransform: "none",
      fontWeight: 600,
      fontSize: "14px",
    },
  },

  shape: {
    borderRadius: 10,
  },

  spacing: 8,

  components: {
    /**
     * 🔘 BUTTONS
     */
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: "8px 14px",
        },
        containedPrimary: {
          backgroundColor: COLORS.brand.primary,
          "&:hover": {
            backgroundColor: COLORS.brand.dark,
          },
        },
      },
    },

    /**
     * 🧾 CARD
     */
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${COLORS.neutral[200]}`,
          boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
        },
      },
    },

    /**
     * 📊 TABLE SYSTEM (IMPORTANT FOR YOU)
     */
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: COLORS.neutral[100],

          "& th": {
            fontWeight: 600,
            fontSize: "13px",
            color: COLORS.neutral[700],
            padding: "10px 12px",
            borderBottom: `1px solid ${COLORS.neutral[200]}`,
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: "13px",
          padding: "10px 12px",
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: COLORS.neutral[50],
          },
        },
      },
    },

    /**
     * 🧾 INPUTS
     */
    MuiTextField: {
      defaultProps: {
        size: "small",
      },
    },

    /**
     * 🧭 APP BAR
     */
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: COLORS.brand.primary,
          color: "#fff",
        },
      },
    },

    /**
     * Chip
     */
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          fontSize: "12px",
          textTransform: "capitalize",
        },
        colorPrimary: {
          backgroundColor: COLORS.neutral[100],
          color: COLORS.brand.primary,
        },
        colorSecondary: {
          backgroundColor: COLORS.status.warningBg,
          color: COLORS.status.warning,
        },
        colorError: {
          backgroundColor: COLORS.status.errorBg,
          color: COLORS.status.error,
        },
        colorInfo: {
          backgroundColor: COLORS.status.infoBg,
          color: COLORS.status.info,
        },
        colorSuccess: {
          backgroundColor: COLORS.status.successBg,
          color: COLORS.status.success
        },
        outlinedPrimary: {
          backgroundColor: 'transparent',
          color: COLORS.status.success,
          borderColor: COLORS.status.success,
        },
        outlinedSecondary: {
          backgroundColor: 'transparent',
          color: COLORS.status.warning,
          borderColor: COLORS.status.warning,

        },
      },
    }
  },
});

export { COLORS };
export default theme;